// FileViewerDockSideBar.jsx
// Display the file viewer dock sidebar.

// Copyright HS Analysis GmbH, 2024
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import { makeStyles } from "@mui/styles";

// external imports
import DockLayout from "rc-dock";

// MUI imports
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Checkbox from "@mui/material/Checkbox";
import FilePreviewList from "./FilePreviewList";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

// HSA Imports
import { FileViewerContext } from "../FileViewer";
import { FileViewerTools } from "../FileViewer";
import CaseInformation from "../../cases/components/CaseInformation";
import CaseList from "./CaseList";
import FilePreviewEditDialog from "./FilePreviewEditDialog";
import Foldertree from "./Foldertree";
import Histogram from "../../viewer/components/Histogram";
import HSAIcon from "../../common/utils/HSAIcon";
import StreamViewTool2 from "../../viewer/components/tools/StreamViewTool2";

export const CaseContext = React.createContext();
const initLayout = {
  dockbox: {
    mode: "horizontal",
    children: [
      {
        size: 226,
        tabs: [{ id: "previewTab" }],
      },
      {
        size: 300,
        tabs: [{ id: "folderTab" }, { id: "caseTab" }],
      },
    ],
  },
};

export default function FileViewerDockSideBar(props) {
  const classes = useStyles();
  const FV = React.useContext(FileViewerContext);

  // State
  const [anchorElCases, setAnchorElCases] = React.useState(null);
  const [anchorElFiles, setAnchorElFiles] = React.useState(null);
  const [anchorElPreview, setAnchorElPreview] = React.useState(null);
  const [anchorElPreviewMenu, setAnchorElPreviewMenu] = React.useState(null);
  const [anchorElFolder, setAnchorElFolder] = React.useState(null);
  const [channelSelectMode, setChannelSelectMode] = React.useState("all");
  const [layout, setLayout] = React.useState(initLayout);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] =
    React.useState(false);

  window.forceSidebarUpdate = () => {
    // changeSideBarLayout();
    // TODO: This is a bad solution, right now without it, updates happen via mouse move
  };

  const menuTypeOpen = Object.freeze({
    PreviewSort: (event) => setAnchorElPreview(event.currentTarget),
    FilesSort: (event) => setAnchorElFiles(event.currentTarget),
    CasesSort: (event) => setAnchorElCases(event.currentTarget),
    FolderSort: (event) => setAnchorElFolder(event.currentTarget),
    PreviewMenu: (event) => setAnchorElPreviewMenu(event.currentTarget),
  });
  const menuTypeHandleSort = Object.freeze({
    Preview: (sortBy) => {
      if (sortBy === FV.filePreviewSortBy) {
        FV.setFilePreviewSortReverse(!FV.filePreviewSortReverse);
      } else {
        FV.setFilePreviewSortBy(sortBy);
      }
    },
    Files: (sortBy) => {
      FV.setFolderSortBy(sortBy);
      FV.setFolderSortReverse(
        sortBy === FV.folderSortBy
          ? !FV.folderSortReverse
          : FV.folderSortReverse
      );
    },
    Cases: (sortBy) => {
      FV.setCaseSortBy(sortBy);
      FV.setCaseSortReverse(
        sortBy === FV.caseSortBy ? !FV.caseSortReverse : FV.caseSortReverse
      );
    },
    Folder: () => {
      FV.setFolderSortReverse(!FV.folderSortReverse);
    },
  });

  const changeChannelSelectMode = (mode) => {
    setChannelSelectMode(mode);
  };

  React.useEffect(() => {
    const url = window.location.href;
    if (url.endsWith("/file_view")) {
      FV.setCaseId(null);
      FV.setSelCase(null);
      FV.resetRenderer();
    }
  }, [window.location.href]);

  React.useEffect(() => {
    changeSideBarLayout();
  }, [FV.caseId]);

  const handleMenuSortSelect = (menuType, sortType) => {
    menuTypeHandleSort[menuType](sortType);
    handleMenuClose();
  };

  const handleMenuClose = () => {
    if (anchorElPreview) {
      setAnchorElPreview(null);
    }
    if (anchorElPreviewMenu) {
      setAnchorElPreviewMenu(null);
    }
    if (anchorElFiles) {
      setAnchorElFiles(null);
    }
    if (anchorElCases) {
      setAnchorElCases(null);
    }
    if (anchorElFolder) {
      setAnchorElFolder(null);
    }
  };

  const changeSideBarLayout = () => {
    if (FV.caseId === null || FV.caseId === undefined) {
      setLayout({
        dockbox: {
          mode: "horizontal",
          children: [
            {
              size: 226,
              tabs: [{ id: "previewTab" }],
            },
            {
              size: 300,
              tabs: [{ id: "folderTab" }, { id: "caseTab" }],
            },
          ],
        },
      });
    } else {
      setLayout({
        dockbox: {
          mode: "horizontal",
          children: [
            {
              tabs: [{ id: "slideFolderTab" }],
            },

            {
              tabs: [{ id: "caseInformationTab" }, { id: "caseTab" }],
            },
          ],
        },
      });
    }
  };

  /**
   * Renders the histogram component if the necessary data is available.
   *
   * @returns {JSX.Element} The histogram component or an empty div if data is not available.
   */
  const renderHistogram = () => {
    if (!FV || !FV.isHistogramVisible) {
      return <div></div>;
    }
    return (
      <div className={classes.histogramContent}>
        {FV.activeFileId &&
          FV.omeDict &&
          FV.omeDict[FV.activeFileId] &&
          FV.histogramConfigs[FV.activeFileId] && (
            <Histogram
              key={FV.activeFileId}
              visible={FV.isHistogramVisible}
              sideBarWidth={400}
              histogramConfig={FV.histogramConfigs[FV.activeFileId]}
              bitDepth={FV.omeDict[FV.activeFileId].bitDepth}
              id={FV.activeFileId}
              channelSelectMode={channelSelectMode}
              changeChannelSelectMode={changeChannelSelectMode}
              onChange={FV.onChangeChannels}
              className={classes.histogramContent}
              fileName={FV.activeFileName}
            />
          )}
      </div>
    );
  };

  let groups = {
    custom: {
      floatable: false,
      maximizable: false,
    },
  };

  let folderTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>FILES</div>
        <Tooltip disableInteractive title="Sort by">
          <IconButton onClick={menuTypeOpen.FilesSort}>
            <HSAIcon icon="SortIcon" size={28} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    content: <Foldertree />,
    group: "custom",
  };

  const previewTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>PREVIEW</div>
        <Tooltip disableInteractive title="Sort by">
          <IconButton onClick={menuTypeOpen.PreviewSort}>
            <HSAIcon icon="SortIcon" size={28} />
          </IconButton>
        </Tooltip>
        <Tooltip disableInteractive title="Preview options">
          <IconButton onClick={menuTypeOpen.PreviewMenu}>
            <HSAIcon icon="MoreVertIcon" size={28} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    content: <FilePreviewList />,
    group: "custom",
  };

  const caseTab = {
    title: (
      <div className={classes.previewTabTitle}>
        {/* <FontAwesomeIcon icon={faCog} size="lg" /> */}
        <div>CASES</div>
        <Tooltip disableInteractive title="Sort by">
          <IconButton onClick={menuTypeOpen.CasesSort}>
            <HSAIcon icon="SortIcon" size={28} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    content: <CaseList />,
    group: "custom",
  };

  const slideFolderTab = {
    title: (
      <div className={classes.previewTabTitle}>
        <div>PREVIEW</div>
        <Tooltip disableInteractive title="Sort by">
          <IconButton onClick={menuTypeOpen.PreviewSort}>
            <HSAIcon icon="SortIcon" size={28} />
          </IconButton>
        </Tooltip>
        <Tooltip disableInteractive title="Preview options">
          <IconButton onClick={menuTypeOpen.PreviewMenu}>
            <HSAIcon icon="MoreVertIcon" size={28} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    content: <FilePreviewList />,

    group: "custom",
  };

  const caseInformationTab = {
    title: (
      <div>
        {/* <FontAwesomeIcon icon={faCog} size="lg" /> */}
        <div>CASE INFORMATION</div>
      </div>
    ),
    content: <CaseInformation />,
    group: "custom",
  };

  const loadTab = (data) => {
    let { id } = data;
    switch (id) {
      case "folderTab":
        return { ...folderTab, id };
      case "caseTab":
        return { ...caseTab, id };
      case "previewTab":
        return { ...previewTab, id, minWidth: 226 };
      case "caseInformationTab":
        return { ...caseInformationTab, id };
      case "slideFolderTab":
        return { ...slideFolderTab, id, minWidth: 500 };
    }
  };

  const onLayoutChange = (newLayout) => {
    let newChilds = newLayout.dockbox.children;
    let oldChilds = layout.dockbox.children;
    if (JSON.stringify(newChilds) !== JSON.stringify(oldChilds)) {
      setLayout(newLayout);
      // props.persistentStorage.save("SidebarLayout", newLayout);
    }
  };

  const openFilePreviewEditDialog = () => {
    setFilePreviewDialogOpen(true);
    handleMenuClose();
  };

  /**
   * Toggles the visibility of the file name in the file viewer.
   * Updates both the local state (`FV.showAdditionalName`) and persists the new value in localStorage
   * under the key "hsaFileViewerShowFileName".
   */
  const toggleAdditionalName = () => {
    FV.setShowAdditionalName(!FV.showAdditionalName);
    localStorage.setItem("hsaFileViewerShowFileName", !FV.showAdditionalName);
  };

  /**
   * Toggles the visibility of comments in the file viewer.
   * Updates both the local state and persists the preference in localStorage.
   */
  const toggleShowComments = () => {
    FV.setShowComments(!FV.showComments);
    localStorage.setItem("hsaFileViewerShowComments", !FV.showComments);
  };

  const toggleShowLabel = () => {
    FV.setShowLabel(!FV.showLabel);
    localStorage.setItem("hsaFileViewerShowLabel", !FV.showLabel);
  };

  const toggleUseAdditionalNameInsteadOfFilename = () => {
    FV.setUseAdditionalNameInsteadOfFilename(
      !FV.useAdditionalNameInsteadOfFilename
    );
    localStorage.setItem(
      "hsaFileViewerUseDisplayName",
      !FV.useAdditionalNameInsteadOfFilename
    );
  };

  return (
    <CaseContext.Provider value={props}>
      <div className={classes.root} style={{ width: FV.treeWidth }}>
        {renderHistogram()}
        <DockLayout
          layout={layout}
          loadTab={loadTab}
          onLayoutChange={onLayoutChange}
          groups={groups}
          className={classes.dockLayoutContainer}
        />
        {FV.activeTool === FileViewerTools.Stream && (
          <div className={classes.toolContainer}>
            <StreamViewTool2
              rendererDict={FV.rendererDict}
              fileId={FV.activeFileId}
            />
          </div>
        )}

        {FV.tool && (
          <div className={classes.toolConfigContainer}>
            {FV.tool.renderConfiguration()}
          </div>
        )}

        <FilePreviewEditDialog
          open={filePreviewDialogOpen}
          onClose={() => setFilePreviewDialogOpen(false)}
        />

        {/* Menu fewEditDialog open={filePreviewDialogOpen} onClose={() => setFilePreviewDialogOpen(false)} />

        {/* Menu for File Preview Tab */}
        <Menu
          anchorEl={anchorElPreviewMenu}
          open={Boolean(anchorElPreviewMenu)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={openFilePreviewEditDialog}>
            <HSAIcon icon="Edit" size={28} style={{ marginRight: 8 }} />
            <span>Edit Preview</span>
          </MenuItem>
          <MenuItem onClick={toggleAdditionalName}>
            <Checkbox
              size="small"
              checked={FV.showAdditionalName ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Additional Name</span>
          </MenuItem>
          <MenuItem onClick={toggleShowComments}>
            <Checkbox
              size="small"
              checked={FV.showComments ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Comments</span>
          </MenuItem>
          <MenuItem onClick={toggleShowLabel}>
            <Checkbox
              size="small"
              checked={FV.showLabel ?? true}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Show Label</span>
          </MenuItem>
          <MenuItem onClick={toggleUseAdditionalNameInsteadOfFilename}>
            <Checkbox
              size="small"
              checked={FV.useAdditionalNameInsteadOfFilename ?? false}
              style={{ padding: 0, marginRight: 8 }}
            />
            <span>Use Additional Name instead of Filename</span>
          </MenuItem>
        </Menu>

        {/* Sort Menu for File Preview Tab */}
        <Menu
          anchorEl={anchorElPreview}
          open={Boolean(anchorElPreview)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Preview, FV.SortByType.Name)
            }
          >
            <span>Name</span>
            {FV.filePreviewSortBy === FV.SortByType.Name && (
              <span>
                {FV.filePreviewSortReverse ? (
                  <ArrowDownward />
                ) : (
                  <ArrowUpward />
                )}
              </span>
            )}
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(
                FV.MenuType.Preview,
                FV.SortByType.AdditionalName
              )
            }
          >
            <span>Additional Name</span>
            {FV.filePreviewSortBy === FV.SortByType.AdditionalName && (
              <span>
                {FV.filePreviewSortReverse ? (
                  <ArrowDownward />
                ) : (
                  <ArrowUpward />
                )}
              </span>
            )}
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Preview, FV.SortByType.Date)
            }
          >
            <span>Creation Date</span>
            {FV.filePreviewSortBy === FV.SortByType.Date && (
              <span>
                {FV.filePreviewSortReverse ? (
                  <ArrowDownward />
                ) : (
                  <ArrowUpward />
                )}
              </span>
            )}
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Preview, FV.SortByType.Type)
            }
          >
            <span>File Type</span>
            {FV.filePreviewSortBy === FV.SortByType.Type && (
              <span>
                {FV.filePreviewSortReverse ? (
                  <ArrowDownward />
                ) : (
                  <ArrowUpward />
                )}
              </span>
            )}
          </MenuItem>
        </Menu>

        {/* Sort Menu for File Tree Tab */}
        <Menu
          anchorEl={anchorElFiles}
          open={Boolean(anchorElFiles)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Folder, FV.SortByType.Name)
            }
          >
            <span>Folder Name</span>
            {FV.folderSortBy === FV.SortByType.Name && (
              <span>
                {FV.folderSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
        </Menu>

        {/* Sort Menu for Cases Tab */}
        <Menu
          anchorEl={anchorElCases}
          open={Boolean(anchorElCases)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Cases, FV.SortByType.Name)
            }
          >
            <span>Name</span>
            {FV.caseSortBy === FV.SortByType.Name && (
              <span>
                {FV.caseSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Cases, FV.SortByType.Date)
            }
          >
            <span>Creation Date</span>
            {FV.caseSortBy === FV.SortByType.Date && (
              <span>
                {FV.caseSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuSortSelect(FV.MenuType.Cases, FV.SortByType.ChangeDate)
            }
          >
            <span>Change Date</span>
            {FV.caseSortBy === FV.SortByType.ChangeDate && (
              <span>
                {FV.caseSortReverse ? <ArrowDownward /> : <ArrowUpward />}
              </span>
            )}
          </MenuItem>
        </Menu>
      </div>
    </CaseContext.Provider>
  );
}

const useStyles = makeStyles({
  root: {
    height: "100%",
    background: "#fff",
    overflow: "hidden",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    // Allow this grid item to shrink below its content's min width (for collapsed sidebar)
    minWidth: 0,
  },
  previewTabTitle: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    alignItems: "center",
    position: "relative",
    bottom: 6,
  },
  toolContainer: {
    padding: "8px",
    borderTop: "5px solid rgb(235, 235, 235)",
  },
  dockLayoutContainer: {
    position: "relative",
    height: "100%",
    // Important for CSS Grid shrink behavior
    minWidth: 0,
  },
  toolConfigContainer: {
    padding: "8px",
    borderTop: "5px solid rgb(235, 235, 235)",
  },
  widthContainer: {
    height: "100%",
    background: "#fff",
    overflow: "hidden",
    display: "grid",
    gridTemplateRows: "1fr auto",
  },
  histogramContent: {
    borderBottom: "#EBEBEB solid",
    padding: "0 20px",
    minHeight: "200px",
  },
});
